import { useContext, useRef, useEffect, useState } from 'react';
import { VideoPoolContext } from './VideoPoolContext';

export const useVideoPool = () => {
  const context = useContext(VideoPoolContext);
  if (!context) {
    throw new Error('useVideoPool must be used within a VideoPoolProvider');
  }
  return context;
};

// Apply the layout styles a pooled video needs once it is (re)acquired.
// Lives alongside acquisition so consumers never mutate the value this hook returns.
const applyBackgroundVideoStyles = (video: HTMLVideoElement) => {
  video.classList.add('absolute', 'inset-0', 'object-cover');
  video.style.width = '100vw';
  video.style.height = '100vh';
  video.style.maxWidth = 'none';
  video.style.maxHeight = 'none';
};

// Hook for using a video from the pool
export const usePooledVideo = (src: string | undefined, isActive: boolean) => {
  const { pool } = useVideoPool();
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const currentVideoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (src && isActive && !currentVideoRef.current) {
      const acquired = pool.acquire(src);
      applyBackgroundVideoStyles(acquired);
      currentVideoRef.current = acquired;
      setVideo(acquired);

      // Each effect owns exactly the lease it acquired, including StrictMode replay.
      return () => {
        pool.release(acquired);
        currentVideoRef.current = null;
      };
    }
  }, [src, isActive, pool]);

  return src && isActive ? video : null;
};

// Hook for preloading videos
export const useVideoPreload = (sources: string[]) => {
  const { pool } = useVideoPool();

  useEffect(() => {
    sources.forEach((src) => {
      pool.preload(src);
    });
  }, [sources, pool]);
};
